import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { selectCurrentUser } from '../auth/authSlice';
import { useGetUserEvaluationSessionsQuery, useGetEvaluationStatsQuery, useGetCoachingTipsQuery, useGetUserDynamicPersonasQuery } from './userDashboardApiSlice';
import { useGetUserQuery } from '../users/usersApiSlice';
import { useGetQuizStatsQuery, useGetPKEStatsQuery } from '../product_knowledge/productKnowledgeApiSlice';
import { useCreateChatSessionMutation } from '../chat_sessions/chatSessionApiSlice';
import { Container, Loader, Icon } from 'semantic-ui-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  RadialLinearScale,
  Filler,
} from 'chart.js';
import { Line, Radar, Bar, Doughnut } from 'react-chartjs-2';
import moment from 'moment';
import { tagLabel } from '../evaluation_sessions/evaluationRules';
import { FONT, lineComparisonDataset, lineDataset, lineOptions, radarComparisonDataset, radarDataset, radarLabel, radarOptions } from '../../components/chartStyle';
import { SectionTitle, StatTile, StatusPill, cardStyle, scoreColor } from '../../components/reportCards';
import { SKILL_SCORE, SkillScoreLabel, TipLabel, scoreText } from '../../components/listPage';
import { NAV } from '../../navLabels';
import { PAGE_SIZE } from '../../constants';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Filler,
  Title,
  Tooltip,
  Legend
);

const UserDashboardPage = () => {
    const [selectedTag, setSelectedTag] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [startingPersonaId, setStartingPersonaId] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const userIdParam = queryParams.get("userId");

    const [createChatSession] = useCreateChatSessionMutation();

    const handleStartTraining = async (personaId) => {
        setStartingPersonaId(personaId);
        try {
            const result = await createChatSession({ persona: personaId, language: "ko" }).unwrap();
            navigate(`/audio-orb-openai?session_id=${result.id}`);
        } catch (err) {
            console.error("Failed to create chat session:", err);
        } finally {
            setStartingPersonaId(null);
        }
    };

    React.useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const currentUser = useSelector(selectCurrentUser);
    // 회사 평균, and everything measured against it, is for admins only (client request,
    // 2026-10-19). Trainees see their own average and how far they have come from their lowest
    // score. Same rule as the stats endpoint's permission (IsStaffOrContractorAdmin).
    const isAdmin = !!(currentUser?.is_staff || currentUser?.is_contractor_admin);
    
    // If userIdParam is present, fetch that user. Otherwise use currentUser.
    // We only need to fetch if userIdParam is different from currentUser.id, 
    // but for simplicity, if userIdParam exists, we can try to fetch it or just use the ID.
    // To get the username, we should fetch the user details if it's not the current user.

    const { data: fetchedUser } = useGetUserQuery(userIdParam, {
        skip: !userIdParam,
    });

    const targetUser = userIdParam ? fetchedUser : currentUser;
    const targetUserId = userIdParam || currentUser?.id;

    const { data: sessions, isLoading, isError, error } = useGetUserEvaluationSessionsQuery(targetUserId, {
        skip: !targetUserId,
    });

    const { data: stats, isLoading: isStatsLoading } = useGetEvaluationStatsQuery(targetUserId, { skip: !isAdmin || !targetUserId });
    const { data: coachingTips } = useGetCoachingTipsQuery(targetUserId, { skip: !targetUserId });
    const { data: quizStats, isLoading: isQuizStatsLoading } = useGetQuizStatsQuery(targetUserId, { skip: !targetUserId });
    const { data: pkeStats, isLoading: isPKEStatsLoading } = useGetPKEStatsQuery(targetUserId, { skip: !targetUserId });
    const { data: dynamicPersonas } = useGetUserDynamicPersonasQuery(targetUserId, { skip: !targetUserId });

    if (isLoading) return (
        <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader active inline='centered' />
        </div>
    );
    if (isError) return null;

    // Counted as 직원 홈 and 직원 관리 count them (#310): 평가 roleplays only, each one's latest
    // finished evaluation (a regrade keeps the old row), scored as 응대 스킬 점수 on 100 — raw
    // points differ by criteria set (Apple 95, LGU 100).
    const sessionList = sessions?.results || sessions || [];
    const latestPerRoleplay = {};
    sessionList
        .filter((s) => s.score_percent != null && !s.chat_session?.is_practice)
        .forEach((s) => {
            const key = s.chat_session?.id || s.id;
            if (!latestPerRoleplay[key] || new Date(s.created_at) > new Date(latestPerRoleplay[key].created_at)) latestPerRoleplay[key] = s;
        });
    const sortedSessions = Object.values(latestPerRoleplay).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const companyAverage = stats?.global_average ?? null;

    // Process data for Radar Chart (Strengths/Weaknesses by Tag)
    // We aggregate data from all sessions to show overall performance per tag
    const tagStats = {};
    
    sortedSessions.forEach(session => {
        // Check if session has tag_results (populated by backend)
        // Note: The list serializer might not include 'result' field by default.
        // If it's missing, we might need to update the serializer or fetch details.
        // Assuming 'result' is available or we use what we have.
        // If 'result' is not in the list view, we can't do this without fetching details.
        // Let's assume for now we might need to fetch it or it's there.
        // Actually, EvaluationSessionBriefSerializer does NOT include 'result'.
        // We need to update the serializer to include 'result' or at least 'tag_results' if we want to avoid N+1 calls.
        
        // However, for now, let's check if 'result' exists and has 'tag_results'
        if (session.result && session.result.tag_results) {
            Object.entries(session.result.tag_results).forEach(([tag, criteriaList]) => {
                if (tag === 'deduction') return;
                if (!tagStats[tag]) {
                    tagStats[tag] = { total: 0, met: 0, score: 0, max_score: 0 };
                }
                
                criteriaList.forEach(c => {
                    tagStats[tag].total += 1;
                    if (c.is_criterion_met) tagStats[tag].met += 1;
                    // Assuming score is available in criterion_data
                    // If not, we can use is_criterion_met as a proxy (1 or 0)
                    // But let's use the percentage of met criteria for the radar chart
                });
            });
        }
    });

    const radarLabels = Object.keys(tagStats);
    const radarDataPoints = radarLabels.map(tag => {
        const stats = tagStats[tag];
        return stats.total > 0 ? (stats.met / stats.total) * 100 : 0;
    });

    // Shared radar look (components/radarStyle.js), as on the session report.
    const radarData = {
        labels: radarLabels.map((tag, i) => radarLabel(tag, radarDataPoints[i])),
        datasets: [
            radarDataset(radarDataPoints, '내 기준 충족률'),
            ...(stats && stats.tag_averages ? [radarComparisonDataset(radarLabels.map(tag => stats.tag_averages[tag] || 0), '회사 평균')] : []),
        ],
    };

    const chartOptions = radarOptions({
        // The area's key, not its label: the label is a two-line array, which never matched
        // a tag option, so clicking a point selected nothing.
        onClick: (event, elements) => {
            if (elements.length > 0) setSelectedTag(radarLabels[elements[0].index]);
        },
    });

    // Apple's stage names with the guide's Korean gloss (evaluationRules.tagLabel).
    const tagText = tag => [].concat(tagLabel(tag)).join(' · ');
    // Apple's stage order, then U+ and 전반 — not whatever order the data arrived in, which put
    // the default (Discover) off-screen at the end of the chip row.
    const TAG_ORDER = ['connect', 'discover', 'share', 'close', 'uplus', 'overall'];
    const tagRank = tag => (TAG_ORDER.indexOf(tag) + 1) || TAG_ORDER.length + 1;
    const tagOptions = [...radarLabels].sort((a, b) => tagRank(a) - tagRank(b)).map(tag => ({ key: tag, text: tagText(tag), value: tag }));

    // Set initial selected tag if none selected and options exist
    if (!selectedTag && tagOptions.length > 0) {
        setSelectedTag("discover");
    }

    // Calculate data for selected tag analysis
    let tagHistoryData = null;
    let tagCriteriaStats = [];

    if (selectedTag) {
        const history = [];
        const criteriaMap = {}; // text -> { met, total }

        sortedSessions.forEach(session => {
            if (session.result && session.result.tag_results && session.result.tag_results[selectedTag]) {
                const criteriaList = session.result.tag_results[selectedTag];
                
                criteriaList.forEach(c => {
                    if (!criteriaMap[c.criterion_text]) {
                        criteriaMap[c.criterion_text] = { met: 0, total: 0 };
                    }
                    criteriaMap[c.criterion_text].total += 1;
                    if (c.is_criterion_met) {
                        criteriaMap[c.criterion_text].met += 1;
                    }
                });

                const metCount = criteriaList.filter(c => c.is_criterion_met).length;
                const totalCount = criteriaList.length;
                const percentage = totalCount > 0 ? (metCount / totalCount) * 100 : 0;

                history.push({
                    date: session.created_at,
                    percentage: percentage
                });
            }
        });

        tagHistoryData = {
            labels: history.map(h => moment(h.date).format('MM/DD')),
            datasets: [
                lineDataset(history.map(h => h.percentage), '기준 충족률'),
                ...(stats && stats.tag_averages && stats.tag_averages[selectedTag]
                    ? [lineComparisonDataset(history.map(() => stats.tag_averages[selectedTag]), '회사 평균')] : []),
            ]
        };

        tagCriteriaStats = Object.entries(criteriaMap).map(([text, stats]) => ({
            text,
            percentage: (stats.met / stats.total) * 100,
            total: stats.total
        })).sort((a, b) => b.percentage - a.percentage);
    }

    // Shared line look (components/chartStyle.js): no legend unless 회사 평균 is drawn too.
    const chartData = {
        labels: sortedSessions.map(s => moment(s.created_at).format('MM/DD')),
        datasets: [
            lineDataset(sortedSessions.map(s => s.score_percent), SKILL_SCORE),
            ...(companyAverage != null ? [lineComparisonDataset(sortedSessions.map(() => companyAverage), '회사 평균')] : []),
        ],
    };
    const mainChartOptions = lineOptions(chartData.labels, { legend: companyAverage != null, unit: '점' });
    const tagChartOptions = tagHistoryData
        ? lineOptions(tagHistoryData.labels, { legend: tagHistoryData.datasets.length > 1, unit: '%' })
        : null;

    // Comparison Data
    let comparisonData = null;
    let userAverage = 0;
    let trend = null; // 'up', 'down', 'stable'
    
    let lowestScore = null;
    let latestScore = null;

    if (sortedSessions.length > 0) {
        const scores = sortedSessions.map(s => s.score_percent);
        lowestScore = Math.min(...scores);
        latestScore = scores[scores.length - 1];

        // Calculate user average total score.
        const totalSum = sortedSessions.reduce((acc, s) => acc + s.score_percent, 0);
        userAverage = totalSum / sortedSessions.length;

        // Calculate Trend (Last 5 vs Previous 5)
        if (sortedSessions.length >= 2) {
            const recentSessions = sortedSessions.slice(-5);
            const recentAvg = recentSessions.reduce((acc, s) => acc + s.score_percent, 0) / recentSessions.length;
            
            let previousAvg = userAverage; // Default to overall average if not enough history
            if (sortedSessions.length >= 10) {
                const previousSessions = sortedSessions.slice(-10, -5);
                previousAvg = previousSessions.reduce((acc, s) => acc + s.score_percent, 0) / previousSessions.length;
            } else if (sortedSessions.length > 5) {
                 const previousSessions = sortedSessions.slice(0, sortedSessions.length - 5);
                 previousAvg = previousSessions.reduce((acc, s) => acc + s.score_percent, 0) / previousSessions.length;
            }

            const diff = recentAvg - previousAvg;
            if (diff > 1) trend = 'up';
            else if (diff < -1) trend = 'down';
            else trend = 'stable';
        }
        
        if (companyAverage != null) comparisonData = {
            labels: ['나의 평균', '회사 평균'],
            datasets: [
                {
                    label: `평균 ${SKILL_SCORE}`,
                    data: [userAverage, companyAverage],
                    backgroundColor: ['rgba(0, 122, 255, 0.85)', 'rgba(255, 149, 0, 0.85)'],
                    borderRadius: 6,
                    borderWidth: 0,
                },
            ],
        };
    }

    // 제품별 정답률 and 유형별 분석. The data prep is unchanged; the cards are the site's.
    const renderProductCharts = () => {
                // Category color palette
                const CATEGORY_COLORS = {
                    'iphone': { bg: 'rgba(0, 122, 255, 0.85)', bgLight: 'rgba(0, 122, 255, 0.3)', border: '#007AFF' },
                    'mac': { bg: 'rgba(88, 86, 214, 0.85)', bgLight: 'rgba(88, 86, 214, 0.3)', border: '#5856D6' },
                    'ipad': { bg: 'rgba(52, 199, 89, 0.85)', bgLight: 'rgba(52, 199, 89, 0.3)', border: '#34C759' },
                    'watch': { bg: 'rgba(255, 149, 0, 0.85)', bgLight: 'rgba(255, 149, 0, 0.3)', border: '#FF9500' },
                    'airpods': { bg: 'rgba(255, 59, 48, 0.85)', bgLight: 'rgba(255, 59, 48, 0.3)', border: '#FF3B30' },
                    'apple tv': { bg: 'rgba(175, 82, 222, 0.85)', bgLight: 'rgba(175, 82, 222, 0.3)', border: '#AF52DE' },
                    'services': { bg: 'rgba(0, 199, 190, 0.85)', bgLight: 'rgba(0, 199, 190, 0.3)', border: '#00C7BE' },
                    'accessories': { bg: 'rgba(255, 45, 85, 0.85)', bgLight: 'rgba(255, 45, 85, 0.3)', border: '#FF2D55' },
                    'vision': { bg: 'rgba(100, 210, 255, 0.85)', bgLight: 'rgba(100, 210, 255, 0.3)', border: '#64D2FF' },
                    'homepod': { bg: 'rgba(255, 214, 10, 0.85)', bgLight: 'rgba(255, 214, 10, 0.3)', border: '#FFD60A' },
                };
                const DEFAULT_COLOR = { bg: 'rgba(142, 142, 147, 0.85)', bgLight: 'rgba(142, 142, 147, 0.3)', border: '#8E8E93' };

                const getCategoryColor = (cat) => CATEGORY_COLORS[cat] || DEFAULT_COLOR;

                // Merge quiz + PKE by-product data, sorted by quiz attempts, limited to top 10
                const mergedProducts = {};

                // Add quiz data
                if (quizStats?.by_product) {
                    Object.entries(quizStats.by_product).forEach(([pid, p]) => {
                        mergedProducts[pid] = {
                            product_name: p.product_name,
                            category: p.category || '',
                            quiz_total: p.total,
                            quiz_correct: p.correct,
                            quiz_accuracy: p.total > 0 ? Math.round((p.correct / p.total) * 100) : null,
                            pke_avg_score: null,
                            pke_count: 0,
                        };
                    });
                }

                // Overlay PKE data
                if (pkeStats?.by_product) {
                    Object.entries(pkeStats.by_product).forEach(([pid, p]) => {
                        if (mergedProducts[pid]) {
                            mergedProducts[pid].pke_avg_score = p.rate;
                            mergedProducts[pid].pke_count = p.sessions;
                            mergedProducts[pid].pke_correct = p.correct;
                            mergedProducts[pid].pke_checked = p.checked;
                            if (!mergedProducts[pid].category) mergedProducts[pid].category = p.category || '';
                        } else {
                            mergedProducts[pid] = {
                                product_name: p.product_name,
                                category: p.category || '',
                                quiz_total: 0,
                                quiz_correct: 0,
                                quiz_accuracy: null,
                                pke_avg_score: p.rate,
                                pke_count: p.sessions,
                                pke_correct: p.correct,
                                pke_checked: p.checked,
                            };
                        }
                    });
                }

                // Sort by total activity descending (quiz attempts + PKE count), top 10
                const sortedProducts = Object.values(mergedProducts)
                    .sort((a, b) => (b.quiz_total + b.pke_count) - (a.quiz_total + a.pke_count))
                    .slice(0, 10);

                // Group by category for labels & ordering
                const categories = [...new Set(sortedProducts.map(p => p.category || '기타'))];
                const sortedByCategory = [];
                categories.forEach(cat => {
                    sortedProducts.filter(p => (p.category || '기타') === cat).forEach(p => sortedByCategory.push(p));
                });

                const hasQuizData = sortedByCategory.some(p => p.quiz_accuracy !== null);
                const hasPKEData = sortedByCategory.some(p => p.pke_avg_score !== null);

        if (!sortedByCategory.length && !(quizStats && quizStats.total_attempts > 0)) return null;
        return (
            <div style={{ ...cols(sortedByCategory.length > 0 && quizStats?.total_attempts > 0 ? 'repeat(2, 1fr)' : '1fr'), marginTop: isMobile ? '10px' : '16px' }}>
                {sortedByCategory.length > 0 && (
                    <div style={cardStyle}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <h3 style={{ ...cardTitle, margin: 0 }}>제품별 정답률</h3>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                {categories.map(cat => (
                                    <span key={cat} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#86868b' }}>
                                        <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: getCategoryColor(cat).border }} />
                                        {cat}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div style={{ height: isMobile ? '320px' : '360px' }}>
                            <Bar
                                data={{
                                    labels: sortedByCategory.map(p => p.product_name),
                                    datasets: [
                                        ...(hasQuizData ? [{
                                            label: '퀴즈 정답률',
                                            data: sortedByCategory.map(p => p.quiz_accuracy),
                                            backgroundColor: sortedByCategory.map(p => getCategoryColor(p.category || '기타').bg),
                                            borderWidth: 0,
                                            borderRadius: 6,
                                        }] : []),
                                        ...(hasPKEData ? [{
                                            label: '설명 정확도',
                                            data: sortedByCategory.map(p => p.pke_avg_score),
                                            backgroundColor: sortedByCategory.map(p => getCategoryColor(p.category || '기타').bgLight),
                                            borderWidth: 0,
                                            borderRadius: 6,
                                        }] : []),
                                    ],
                                }}
                                options={{
                                    indexAxis: 'y',
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    plugins: {
                                        legend: { display: hasPKEData && hasQuizData, position: 'bottom', labels: { usePointStyle: true, boxWidth: 8, color: '#424245', font: { family: FONT, size: 11 } } },
                                        tooltip: {
                                            backgroundColor: '#1d1d1f', cornerRadius: 8, padding: 8,
                                            callbacks: {
                                                label: (ctx) => `${ctx.dataset.label}: ${Math.round(ctx.raw)}%`,
                                                afterLabel: (ctx) => {
                                                    const p = sortedByCategory[ctx.dataIndex];
                                                    const lines = [];
                                                    if (p.quiz_total > 0) lines.push(`퀴즈: ${p.quiz_correct}/${p.quiz_total}`);
                                                    if (p.pke_checked > 0) lines.push(`설명 정확도: ${p.pke_correct}/${p.pke_checked} · ${p.pke_count}회`);
                                                    return lines.join('\n');
                                                },
                                            },
                                        },
                                    },
                                    scales: {
                                        x: { min: 0, max: 100, grid: { color: 'rgba(0,0,0,0.06)' }, border: { display: false }, ticks: { stepSize: 50, color: '#86868b', font: { family: FONT, size: 11 }, callback: v => v + '%' } },
                                        // Long product names were cut off at the left edge on phones.
                                        y: { grid: { display: false }, border: { display: false }, ticks: { color: '#424245', font: { family: FONT, size: 11 }, callback: function (v) {
                                            const name = this.getLabelForValue(v);
                                            const max = isMobile ? 14 : 28;
                                            return name.length > max ? `${name.slice(0, max - 1)}…` : name;
                                        } } },
                                    },
                                }}
                            />
                        </div>
                    </div>
                )}
                {quizStats && quizStats.total_attempts > 0 && (
                    <div style={cardStyle}>
                        <h3 style={cardTitle}>유형별 분석</h3>
                        {quizStats.mc_attempts + quizStats.oe_attempts === 0 ? (
                            // An empty doughnut read as a broken chart.
                            <div style={{ color: '#86868b', fontSize: '14px', textAlign: 'center', padding: '24px 0' }}>아직 집계할 퀴즈 기록이 없습니다.</div>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap', justifyContent: 'center' }}>
                                <div style={{ width: '160px', height: '160px' }}>
                                    <Doughnut
                                        data={{
                                            labels: ['객관식 정답', '객관식 오답', '서술형 정답', '서술형 오답'],
                                            datasets: [{
                                                data: [
                                                    quizStats.mc_correct,
                                                    quizStats.mc_attempts - quizStats.mc_correct,
                                                    quizStats.oe_correct,
                                                    quizStats.oe_attempts - quizStats.oe_correct,
                                                ],
                                                backgroundColor: ['rgba(0, 122, 255, 0.85)', 'rgba(0, 122, 255, 0.18)', 'rgba(52, 199, 89, 0.85)', 'rgba(52, 199, 89, 0.18)'],
                                                borderWidth: 0,
                                            }],
                                        }}
                                        options={{ responsive: true, maintainAspectRatio: true, cutout: '68%', plugins: { legend: { display: false } } }}
                                    />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: '160px' }}>
                                    {[['객관식', 'rgba(0, 122, 255, 0.85)', quizStats.mc_accuracy, quizStats.mc_correct, quizStats.mc_attempts],
                                      ['서술형', 'rgba(52, 199, 89, 0.85)', quizStats.oe_accuracy, quizStats.oe_correct, quizStats.oe_attempts]].map(([label, color, acc, ok, n]) => (
                                        <div key={label}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#86868b' }}>
                                                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color }} />{label}
                                            </div>
                                            <div style={{ fontSize: '20px', fontWeight: 700, color: '#1d1d1f' }}>
                                                {acc}%<span style={{ fontSize: '12px', fontWeight: 400, color: '#86868b', marginLeft: '6px' }}>{ok}/{n}</span>
                                            </div>
                                        </div>
                                    ))}
                                    {/* Questions by their latest answer, not attempts (#249). */}
                                    <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '10px', fontSize: '13px', color: '#424245' }}>
                                        문제 기준 <span style={{ color: '#34C759', fontWeight: 600 }}>{quizStats.correct_attempts} 정답</span>
                                        {' · '}
                                        <span style={{ color: '#FF3B30', fontWeight: 600 }}>{(quizStats.questions_answered ?? quizStats.total_attempts) - quizStats.correct_attempts} 오답</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // Layout: the shared header band, SectionTitle sections, StatTile tiles and plain cards
    // on the page Container's gutter (docs/FRONTEND_UI.md) — not Semantic Grid/Card/Header.
    const cols = (desktop, mobile = '1fr') => ({ display: 'grid', gridTemplateColumns: isMobile ? mobile : desktop, gap: isMobile ? '10px' : '16px' });
    const cardTitle = { fontSize: '15px', fontWeight: 600, color: '#1d1d1f', margin: '0 0 12px' };
    const empty = (text) => <div style={{ ...cardStyle, textAlign: 'center', color: '#86868b', fontSize: '14px', padding: '28px 16px' }}>{text}</div>;
    const strengths = tagCriteriaStats.filter(c => c.percentage >= 80).slice(0, isMobile ? 3 : 5);
    const toImprove = tagCriteriaStats.filter(c => c.percentage < 80).reverse().slice(0, isMobile ? 3 : 5);
    const trendLabel = trend === 'up' ? '상승세' : trend === 'down' ? '하락세' : trend === 'stable' ? '유지' : null;
    const salesTiles = [
        ...(isAdmin && companyAverage != null ? [{ label: '회사 평균', value: scoreText(companyAverage), color: '#86868b', sub: stats.partner_name }] : []),
        { label: <SkillScoreLabel />, id: 'avg', value: scoreText(userAverage), sub: ['평균', latestScore != null && `최근 ${scoreText(latestScore)}`, trendLabel].filter(Boolean).join(' · ') },
        sortedSessions.length < 2
            ? { label: '최저 점수 대비', value: '—', color: '#c7c7cc', sub: '평가 2회부터 표시' }
            : { label: '최저 점수 대비', value: `${latestScore > lowestScore ? '+' : ''}${Math.round(latestScore - lowestScore)}점`, color: latestScore > lowestScore ? '#34C759' : '#1d1d1f', sub: `최저 ${scoreText(lowestScore)} → 최근 ${scoreText(latestScore)}` },
        ...(isAdmin && companyAverage != null ? [{ label: '평균 대비', value: `${userAverage >= companyAverage ? '+' : '−'}${Math.round(Math.abs(userAverage - companyAverage))}점`, color: userAverage >= companyAverage ? '#34C759' : '#FF3B30' }] : []),
    ];

    // One criterion's rate in the selected area: text, a thin bar, "75% · 16회".
    const CriterionRate = ({ c, good }) => (
        <div style={{ padding: '6px 0' }}>
            <div style={{ fontSize: '13.5px', lineHeight: 1.5, color: '#1d1d1f', wordBreak: 'keep-all' }}>{c.text}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                <div style={{ flex: 1, height: '6px', borderRadius: '3px', backgroundColor: '#F2F2F7', overflow: 'hidden' }}>
                    <div style={{ width: `${c.percentage}%`, height: '100%', borderRadius: '3px', backgroundColor: good ? '#34C759' : '#FF3B30' }} />
                </div>
                <span style={{ fontSize: '12px', color: '#86868b', whiteSpace: 'nowrap' }}>{c.percentage.toFixed(0)}% · {c.total}회</span>
            </div>
        </div>
    );
    const rateList = (label, color, items, none, good) => (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: 600, color: '#1d1d1f', marginBottom: '4px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color }} />
                {label}
            </div>
            {items.length ? items.map((c, i) => <CriterionRate key={i} c={c} good={good} />) : <div style={{ fontSize: '13px', color: '#86868b', padding: '6px 0' }}>{none}</div>}
        </div>
    );

    return (
        <div style={{ backgroundColor: '#f9fafb', minHeight: 'calc(100vh - 50px)' }}>
            <div className="page-header-band" style={{
                padding: '16px 0',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                borderBottom: '1px solid rgba(0,0,0,0.05)',
                position: 'relative',
                zIndex: 100,
            }}>
                <Container>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                            <div className="header-icon-desktop-only" style={{ width: '40px', height: '40px', flexShrink: 0, borderRadius: '50%', backgroundColor: '#F2F2F7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Icon name="dashboard" className="centered-icon" style={{ color: '#007AFF', fontSize: '18px' }} />
                            </div>
                            <div style={{ minWidth: 0 }}>
                                {/* The menu's name for your own dashboard; an admin looking at a trainee's
                                    sees whose it is in the subtitle. */}
                                <h2 style={{ fontSize: '17px', fontWeight: 600, margin: 0, color: '#1d1d1f' }}>{userIdParam ? '사용자 대시보드' : NAV.DASHBOARD}</h2>
                                <p style={{ fontSize: '13px', color: '#86868b', margin: 0 }}>{targetUser?.full_name || targetUser?.username}</p>
                            </div>
                        </div>
                        <Link
                            to={`/chatsessionslist?trainee=${targetUserId}&page_size=${PAGE_SIZE}`}
                            style={{ flexShrink: 0, padding: '8px 14px', borderRadius: '999px', backgroundColor: '#F2F2F7', color: '#1d1d1f', fontSize: '13px', fontWeight: 600 }}
                        >
                            {NAV.SESSIONS}
                        </Link>
                    </div>
                </Container>
            </div>

            <Container style={{ paddingBottom: '40px' }}>
                {/* ── 판매 스킬 ── */}
                <SectionTitle first sub="평가 롤플레이 결과">응대 스킬</SectionTitle>
                {sortedSessions.length > 0 ? (
                    <>
                        {isAdmin && companyAverage != null && (() => {
                            const diff = userAverage - companyAverage;
                            const [tint, title, msg] = diff >= 2
                                ? ['#f2fcf5', '훌륭합니다!', '전체 평균보다 높은 성과를 유지하고 있습니다.']
                                : diff <= -2
                                    ? ['#fff5f5', '분발하세요!', '전체 평균보다 다소 낮습니다. 개선이 필요합니다.']
                                    : ['#fffaf0', '평균적인 성과입니다.', '전체 평균과 비슷한 수준입니다.'];
                            return (
                                <div style={{ ...cardStyle, backgroundColor: tint, marginBottom: isMobile ? '10px' : '16px' }}>
                                    <div style={{ fontSize: '15px', fontWeight: 600, color: '#1d1d1f' }}>{title}</div>
                                    <div style={{ fontSize: '14px', color: '#424245', marginTop: '2px' }}>{msg}</div>
                                </div>
                            );
                        })()}
                        <div style={cols(`repeat(${salesTiles.length}, 1fr)`, '1fr 1fr')}>
                            {salesTiles.map(({ id, ...t }) => <StatTile key={id || t.label} {...t} />)}
                        </div>
                    </>
                ) : empty('아직 평가받은 롤플레이가 없습니다.')}

                {/* ── AI 코칭 인사이트 ── */}
                {coachingTips && coachingTips.length > 0 && (
                    <>
                        <SectionTitle sub="최근 평가에서 반복해서 놓친 기준과 개선 방법">AI 코칭 인사이트</SectionTitle>
                        <div style={cols('repeat(3, 1fr)')}>
                            {coachingTips.map((tip, index) => (
                                <div
                                    key={index}
                                    // Opens the evaluation the tip's text comes from (the latest one it failed in).
                                    onClick={tip.chat_session_id ? () => navigate(`/chatsessions/${tip.chat_session_id}`) : undefined}
                                    style={{ ...cardStyle, cursor: tip.chat_session_id ? 'pointer' : 'default' }}
                                >
                                    <StatusPill favorable={false}>
                                        {tip.sessions_considered ? `최근 ${tip.sessions_considered}회 중 ${tip.failure_count}회 미충족` : `최근 ${tip.failure_count}회 미충족`}
                                    </StatusPill>
                                    <div style={{ fontSize: '15px', fontWeight: 600, lineHeight: 1.45, color: '#1d1d1f', margin: '10px 0 6px', wordBreak: 'keep-all' }}>{tip.criterion}</div>
                                    <div style={{ fontSize: '14px', lineHeight: 1.6, color: '#424245' }}>{tip.tip}</div>
                                    {tip.chat_session_id && <div style={{ marginTop: '10px', fontSize: '13px', fontWeight: 600, color: '#007AFF' }}>평가 보기 ›</div>}
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {/* ── 성과 분석 ── */}
                <SectionTitle sub={`${SKILL_SCORE} 추이와 영역별 기준 충족률`}>성과 분석</SectionTitle>
                <div style={cols(isAdmin ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)')}>
                    <div style={cardStyle}>
                        <h3 style={cardTitle}><SkillScoreLabel /> 추이</h3>
                        {sortedSessions.length > 0
                            ? <div style={{ height: isMobile ? '220px' : '280px' }}><Line options={mainChartOptions} data={chartData} /></div>
                            : <div style={{ color: '#86868b', fontSize: '14px' }}>아직 평가받은 롤플레이가 없습니다.</div>}
                    </div>
                    <div style={cardStyle}>
                        <h3 style={cardTitle}><TipLabel hint="criteriaMet">영역별 성과</TipLabel></h3>
                        {radarLabels.length > 0
                            ? <div style={{ height: isMobile ? '260px' : '280px' }}><Radar data={radarData} options={chartOptions} /></div>
                            : <div style={{ color: '#86868b', fontSize: '14px' }}>영역별 결과가 아직 없습니다.</div>}
                    </div>
                    {isAdmin && (
                        <div style={cardStyle}>
                            <h3 style={cardTitle}>동료 비교</h3>
                            {comparisonData && (
                                <div style={{ height: '280px' }}>
                                    <Bar
                                        data={comparisonData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            indexAxis: 'y',
                                            plugins: { legend: { display: false } },
                                            scales: {
                                                x: { min: 0, max: 100, grid: { color: 'rgba(0,0,0,0.06)' }, border: { display: false }, ticks: { stepSize: 50, color: '#86868b', font: { family: FONT, size: 11 } } },
                                                y: { grid: { display: false }, border: { display: false }, ticks: { color: '#424245', font: { family: FONT, size: 12, weight: '600' } } },
                                            },
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── 영역별 상세 ── */}
                {tagOptions.length > 0 && (
                    <>
                        <SectionTitle sub="영역을 골라 추이와 강점·보완점을 확인하세요">영역별 상세</SectionTitle>
                        <div style={cardStyle}>
                            {/* Pills, as 롤플레이 기록's filters; they scroll sideways on a phone. */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                                {tagOptions.map((t) => (
                                    <button
                                        key={t.value}
                                        type="button"
                                        onClick={() => setSelectedTag(t.value)}
                                        style={{ flexShrink: 0, border: 'none', borderRadius: '999px', padding: '7px 14px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', backgroundColor: selectedTag === t.value ? '#1d1d1f' : '#F2F2F7', color: selectedTag === t.value ? 'white' : '#424245' }}
                                    >
                                        {t.text}
                                    </button>
                                ))}
                            </div>
                            {selectedTag && tagHistoryData && (
                                <div style={cols('repeat(3, 1fr)')}>
                                    <div>
                                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1d1d1f', marginBottom: '8px' }}><TipLabel hint="criteriaMet">기준 충족률 추이</TipLabel></div>
                                        <div style={{ height: '200px' }}><Line data={tagHistoryData} options={tagChartOptions} /></div>
                                    </div>
                                    {rateList('강점', '#34C759', strengths, '아직 식별된 강점이 없습니다.', true)}
                                    {rateList('보완 필요', '#FF3B30', toImprove, '식별된 보완점이 없습니다.', false)}
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* ── 맞춤 훈련 고객 ── */}
                {dynamicPersonas?.results?.length > 0 && (
                    <>
                        <SectionTitle sub="취약 영역을 연습할 수 있도록 만든 맞춤형 고객">맞춤 훈련 페르소나</SectionTitle>
                        <div style={cols('repeat(3, 1fr)')}>
                            {dynamicPersonas.results.map((persona) => {
                                const aoi = persona.areas_of_improvement;
                                const comm = aoi?.communication || [];
                                const products = aoi?.product_knowledge || [];
                                const productNames = products.map(p => p.name).filter(Boolean);
                                const inactive = !!persona.deactivated_at;
                                const listBlock = (label, items) => items.length > 0 && (
                                    <div style={{ marginTop: '8px' }}>
                                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#86868b', marginBottom: '2px' }}>{label}</div>
                                        <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13.5px', lineHeight: 1.6, color: '#424245' }}>
                                            {items.map((t, i) => <li key={i}>{t}</li>)}
                                        </ul>
                                    </div>
                                );
                                return (
                                    <div key={persona.id} style={{ ...cardStyle, display: 'flex', flexDirection: 'column', opacity: inactive ? 0.6 : 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: '15px', fontWeight: 600, color: '#1d1d1f' }}>{persona.name}</span>
                                            {inactive && <span style={{ padding: '2px 8px', borderRadius: '999px', backgroundColor: '#F2F2F7', color: '#86868b', fontSize: '11px', fontWeight: 600 }}>비활성</span>}
                                        </div>
                                        <span style={{ alignSelf: 'flex-start', marginTop: '8px', padding: '3px 10px', borderRadius: '999px', backgroundColor: '#FFF4E5', color: '#8A4B00', fontSize: '12px', fontWeight: 600 }}>
                                            {productNames.length ? productNames.join(', ') : '훈련'}
                                        </span>
                                        {comm.length === 0 && products.length === 0
                                            ? <div style={{ marginTop: '8px', fontSize: '13.5px', lineHeight: 1.6, color: '#424245' }}>{persona.purpose_of_visit || ''}</div>
                                            : <>{listBlock('응대 스킬', comm.map(c => c.text).filter(Boolean))}{listBlock('제품', productNames)}</>}
                                        {/* Bottom-aligned, so the buttons line up across cards of different length. */}
                                        {!inactive && (
                                            <div style={{ marginTop: 'auto', paddingTop: '14px' }}>
                                                <button
                                                    type="button"
                                                    disabled={startingPersonaId === persona.id}
                                                    onClick={() => handleStartTraining(persona.id)}
                                                    style={{ width: '100%', height: '42px', border: 'none', borderRadius: '999px', backgroundColor: '#007AFF', color: 'white', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                                                >
                                                    {startingPersonaId === persona.id ? '준비 중…' : '훈련 시작'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* ── 제품 지식 ── */}
                <SectionTitle sub="롤플레이 설명 정확도와 퀴즈 기록">제품 지식</SectionTitle>
                {(isQuizStatsLoading || isPKEStatsLoading) ? (
                    <div style={{ ...cardStyle, padding: '32px' }}><Loader active inline="centered" /></div>
                ) : (quizStats && quizStats.total_attempts > 0) || (pkeStats && pkeStats.total_evaluations > 0) ? (
                    <>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(150px, 1fr))', gap: isMobile ? '10px' : '16px' }}>
                            {pkeStats && pkeStats.total_evaluations > 0 && (
                                <>
                                    <StatTile label="설명 평가" value={`${pkeStats.total_evaluations}회`} />
                                    {/* Pooled over the claims (#283): correct ÷ checked across sessions, not a mean of scores. */}
                                    {pkeStats.checked > 0 && (
                                        <StatTile label="정확도" value={`${Math.round(pkeStats.rate)}%`} color={scoreColor(pkeStats.rate)} sub={`정확 ${pkeStats.correct} / ${pkeStats.checked}`} />
                                    )}
                                </>
                            )}
                            {quizStats && quizStats.total_attempts > 0 && (
                                <>
                                    {/* accuracy = latest answer per question (what they know now); first try =
                                        what they knew before practising. Repeats inflate neither (#249). */}
                                    <StatTile
                                        label="퀴즈 정답률"
                                        value={`${quizStats.accuracy}%`}
                                        color={scoreColor(quizStats.accuracy)}
                                        sub={quizStats.first_try_accuracy !== undefined ? `첫 시도 ${quizStats.first_try_accuracy}%` : null}
                                    />
                                    {/* Coverage plus effort: the attempt count alone said nothing (793 could be
                                        793 questions or the same 5 on a loop). */}
                                    <StatTile
                                        label="풀이 기록"
                                        value={`${quizStats.questions_covered ?? quizStats.questions_answered}${quizStats.questions_available > 0 ? ` / ${quizStats.questions_available}` : ''}`}
                                        sub={`총 ${quizStats.total_attempts}회${quizStats.questions_answered > 0 ? ` · 문제당 ${Math.round(quizStats.total_attempts / quizStats.questions_answered)}회` : ''}`}
                                    />
                                    <StatTile label="연속 학습" value={quizStats.current_streak > 0 ? `🔥 ${quizStats.current_streak}일` : '—'} />
                                    <StatTile
                                        label="복습 필요"
                                        value={`${quizStats.due_for_review}`}
                                        color={quizStats.due_for_review > 0 ? '#FF9500' : '#1d1d1f'}
                                        tint={quizStats.due_for_review > 0 ? '#FFF8E1' : undefined}
                                    />
                                </>
                            )}
                        </div>
                        {renderProductCharts()}
                    </>
                ) : (
                    <div style={{ ...cardStyle, textAlign: 'center', padding: '28px 16px' }}>
                        <div style={{ color: '#86868b', fontSize: '14px' }}>아직 퀴즈 학습 기록이 없습니다.</div>
                        {!userIdParam && (
                            <Link to="/quiz/dashboard" style={{ display: 'inline-block', marginTop: '12px', padding: '8px 16px', borderRadius: '999px', backgroundColor: '#007AFF', color: 'white', fontSize: '14px', fontWeight: 600 }}>
                                퀴즈 시작하기
                            </Link>
                        )}
                    </div>
                )}
            </Container>
        </div>
    );

};

export default UserDashboardPage;
